/**
 * Admin org management store for the Social Media SaaS app.
 *
 * Super-admins can switch between orgs. The selected org_id is persisted
 * in localStorage so a page refresh keeps the selection. When a non-null
 * selectedOrgId is present, the API client injects X-Tenant-Id on every
 * request, overriding the hostname-resolved org on the backend.
 *
 * @module lib/admin
 */

import { createAppStore } from "@plerity/dashboard-shared/stores";
import { persist } from "zustand/middleware";

interface AdminState {
  /** Super-admin override org_id — null means use hostname-resolved org */
  selectedOrgId: number | null;
  setSelectedOrgId: (orgId: number | null) => void;
  reset: () => void;
}

/**
 * The caller's OWN org from the unified `animas_auth` JWT (JS-readable by
 * design — see `@plerity/auth` AuthProvider `readAuthCookie`). Decoded without
 * a signature check: it only chooses which tenant to ASK for; social-api
 * re-verifies the token and 403s any tenant the principal does not own.
 * Super-admins and org-less tokens return null (keep the hostname default).
 */
function ownOrgIdFromAuthCookie(): number | null {
  if (typeof document === "undefined") return null;
  const entry = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("animas_auth="));
  if (!entry) return null;
  try {
    const payload = decodeURIComponent(entry.slice("animas_auth=".length)).split(".")[1] ?? "";
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "="))) as {
      org_id?: unknown;
      is_super_admin?: unknown;
    };
    if (claims.is_super_admin === true) return null;
    return typeof claims.org_id === "number" && claims.org_id > 0 ? claims.org_id : null;
  } catch {
    return null;
  }
}

/**
 * Tenant to send as `X-Tenant-Id`: the admin switcher's selection, else the
 * caller's own JWT org (non-super-admins).
 *
 * Without the fallback a client-org user (any org other than the hostname
 * default, org 1) sent NO header, the backend resolved the default org 1, and
 * `enforce_principal_tenant` 403'd every data call ("org_id mismatch between
 * JWT and tenant context") — the shell rendered but every page was empty/erroring
 * (found 2026-10-09 provisioning the Meta App Review reviewer, org 1299).
 * Users whose JWT org IS 1 send `X-Tenant-Id: 1` — identical to the default.
 */
export function getEffectiveTenantId(): number | null {
  return useAdminStore.getState().selectedOrgId ?? ownOrgIdFromAuthCookie();
}

export const useAdminStore = createAppStore<AdminState>(
  "social-admin",
  persist(
    (set) => ({
      selectedOrgId: null,
      setSelectedOrgId: (orgId) => set({ selectedOrgId: orgId }),
      reset: () => set({ selectedOrgId: null }),
    }),
    {
      name: "social-admin-org",
      partialize: (state) => ({ selectedOrgId: state.selectedOrgId }),
    }
  )
);
